import { useState } from "react";
import { CheckCircle2, XCircle, Clock3, CalendarDays, RefreshCw, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

interface LeaveApprovalsTabProps {
  leaveAdmin: any;
  decideLeave: any;
  idToken: string | null;
}

export function LeaveApprovalsTab({
  leaveAdmin,
  decideLeave,
  idToken,
}: LeaveApprovalsTabProps) {
  const [decisionNotes, setDecisionNotes] = useState<Record<number, string>>({});
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  if (leaveAdmin.isLoading) {
    return (
      <Card className="glass-panel rounded-3xl p-8 text-center">
        <RefreshCw className="mx-auto mb-3 animate-spin text-[#1b6b44]" size={28} />
        <p className="font-black text-slate-800">กำลังโหลดคำขอวันลา…</p>
      </Card>
    );
  }
  if (leaveAdmin.error) {
    return (
      <Card className="glass-panel rounded-3xl border-rose-200 bg-rose-50/90 p-6 text-center">
        <p className="font-black text-rose-800">โหลดคำขอวันลาไม่สำเร็จ</p>
        <p className="mt-1 text-xs text-rose-600">{leaveAdmin.error.message}</p>
      </Card>
    );
  }
  if (!leaveAdmin.data) return null;

  const requests = leaveAdmin.data.requests || [];
  const pendingRequests = requests.filter((r: any) => r.status === "pending");
  const allPendingSelected = pendingRequests.length > 0 && pendingRequests.every((r: any) => selectedIds.includes(r.id));

  const handleBulkDecision = async (status: "approved" | "rejected") => {
    if (!idToken || selectedIds.length === 0) return;
    await Promise.all(
      selectedIds.map((id) =>
        decideLeave.mutateAsync({
          idToken,
          id,
          status,
          note: decisionNotes[id] ?? "",
        })
      )
    );
    setSelectedIds([]);
  };

  return (
    <div className="space-y-4">
      <Card className="glass-panel overflow-hidden rounded-3xl border border-[#e5ede7] shadow-lg">
        <div className="h-1.5 bg-gradient-to-r from-[#1b6b44] via-[#248a58] to-[#6ee7b7]" />
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="flex h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs font-black uppercase tracking-wider text-[#1b6b44]">
                  ฝ่ายบริหาร / HR Management
                </span>
              </div>
              <CardTitle className="text-xl sm:text-2xl font-black text-slate-900">
                อนุมัติคำขอวันลา
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm text-slate-500 font-medium">
                ปีงบประมาณ {leaveAdmin.data.period.fiscalYear} · รออนุมัติ {pendingRequests.length} รายการ
              </CardDescription>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => leaveAdmin.refetch()}
              className="rounded-full border-[#cbe4d2] text-[#1b6b44] hover:bg-[#ebf6ee] text-xs font-bold"
            >
              <RefreshCw size={13} className={`mr-1.5 ${leaveAdmin.isFetching ? "animate-spin" : ""}`} />
              รีเฟรชข้อมูล
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-3">
          {pendingRequests.length > 0 && (
            <div className="sticky top-[72px] z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-2xl border border-emerald-200 bg-white/95 p-3 shadow-sm backdrop-blur">
              <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-extrabold text-slate-700">
                <Checkbox
                  checked={allPendingSelected}
                  onCheckedChange={(checked) =>
                    setSelectedIds(checked ? pendingRequests.map((r: any) => r.id) : [])
                  }
                  aria-label="เลือกคำขอที่รออนุมัติทั้งหมด"
                  className="h-5 w-5 border-2 border-emerald-700 data-[state=checked]:bg-emerald-700"
                />
                เลือกทั้งหมด
                <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                  เลือกแล้ว {selectedIds.length} รายการ
                </span>
              </label>
              <div className="grid grid-cols-2 gap-2 sm:flex">
                <Button
                  className="btn-primary-teal h-10 rounded-xl text-xs font-black"
                  disabled={!selectedIds.length || decideLeave.isPending}
                  onClick={() => void handleBulkDecision("approved")}
                >
                  <CheckCircle2 size={15} className="mr-1" /> อนุมัติที่เลือก
                </Button>
                <Button
                  variant="outline"
                  className="h-10 rounded-xl border-rose-200 bg-rose-50 text-xs font-black text-rose-700"
                  disabled={!selectedIds.length || decideLeave.isPending}
                  onClick={() => void handleBulkDecision("rejected")}
                >
                  <XCircle size={15} className="mr-1" /> ไม่อนุมัติ
                </Button>
              </div>
            </div>
          )}
          {requests.length === 0 ? (
            <div className="p-8 text-center rounded-2xl border border-slate-200">
              <CalendarDays className="mx-auto text-slate-400 mb-2" size={32} />
              <p className="text-sm font-bold text-slate-600">ไม่มีคำขอวันลาในระบบ</p>
            </div>
          ) : (
            requests.map((r: any) => (
              <div
                key={r.id}
                className="rounded-2xl border border-slate-200/80 bg-white/90 p-4 shadow-2xs hover:border-[#1b6b44]/40 transition"
              >
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                  {r.status === "pending" && (
                    <Checkbox
                      checked={selectedIds.includes(r.id)}
                      onCheckedChange={(checked) =>
                        setSelectedIds((prev) =>
                          checked
                            ? prev.includes(r.id) ? prev : prev.concat(r.id)
                            : prev.filter((id) => id !== r.id)
                        )
                      }
                      aria-label={`เลือกคำขอของ ${r.displayName}`}
                      className="h-5 w-5 shrink-0 border-2 border-emerald-700 data-[state=checked]:bg-emerald-700"
                    />
                  )}
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-black text-sm sm:text-base text-slate-900">
                        {r.displayName}
                      </span>
                      <span className="text-xs font-bold text-slate-500 font-numeric">
                        · {r.startDate} ถึง {r.endDate}
                      </span>
                      <Badge className="bg-[#ebf6ee] text-[#1b6b44] border border-[#cbe4d2] text-[11px] font-bold">
                        {r.days} วัน
                      </Badge>

                      <div className="ml-auto lg:ml-0">
                        {r.status === "pending" && (
                          <Badge className="bg-amber-100 text-amber-900 border border-amber-300 text-[10px] font-bold">
                            <Clock3 size={11} className="mr-1 text-amber-700" /> รออนุมัติ
                          </Badge>
                        )}
                        {r.status === "approved" && (
                          <Badge className="bg-[#ebf6ee] text-[#134e31] border border-[#cbe4d2] text-[10px] font-bold">
                            <CheckCircle2 size={11} className="mr-1 text-[#1b6b44]" /> อนุมัติแล้ว
                          </Badge>
                        )}
                        {r.status === "rejected" && (
                          <Badge className="bg-rose-100 text-rose-900 border border-rose-300 text-[10px] font-bold">
                            <XCircle size={11} className="mr-1 text-rose-700" /> ไม่อนุมัติ
                          </Badge>
                        )}
                      </div>
                    </div>

                    <p className="text-xs text-slate-700">
                      <strong>เหตุผล:</strong> {r.reason || "ไม่ระบุเหตุผล"}
                    </p>

                    {r.handoverTo && (
                      <p className="text-[11px] text-slate-500">
                        <strong>ส่งมอบงานให้:</strong> {r.handoverTo}{" "}
                        {r.handoverDetails ? `(${r.handoverDetails})` : ""}
                      </p>
                    )}
                  </div>

                  {r.status === "pending" ? (
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0 pt-2 lg:pt-0 border-t lg:border-t-0 border-slate-100">
                      <Input
                        placeholder="หมายเหตุ (ถ้ามี)..."
                        value={decisionNotes[r.id] ?? ""}
                        onChange={(e) =>
                          setDecisionNotes((prev) => ({ ...prev, [r.id]: e.target.value }))
                        }
                        className="text-xs h-9 rounded-xl border-slate-200 bg-white sm:w-44"
                      />

                      <div className="flex gap-2">
                        <Button
                          className="btn-primary-teal h-9 text-xs font-black rounded-xl shadow-xs"
                          disabled={decideLeave.isPending}
                          onClick={() =>
                            idToken &&
                            decideLeave.mutate({
                              idToken,
                              id: r.id,
                              status: "approved",
                              note: decisionNotes[r.id] ?? "",
                            })
                          }
                        >
                          <CheckCircle2 size={14} className="mr-1" />
                          อนุมัติ
                        </Button>
                        <Button
                          variant="outline"
                          className="h-9 text-xs font-bold rounded-xl border-rose-200 bg-rose-50/70 text-rose-700 hover:bg-rose-100 hover:text-rose-800"
                          disabled={decideLeave.isPending}
                          onClick={() =>
                            idToken &&
                            decideLeave.mutate({
                              idToken,
                              id: r.id,
                              status: "rejected",
                              note: decisionNotes[r.id] ?? "",
                            })
                          }
                        >
                          <XCircle size={14} className="mr-1" />
                          ไม่อนุมัติ
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}
